import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { userApi } from "@/api/user";
import { USER_QUERY_KEY, useUser } from "@/modules/shared/hooks/use-user";
import { logMsg, logEx, crumb } from "@/config/sentry-logger";

type AddReferralsResponse = Awaited<ReturnType<typeof userApi.addReferrals>>;

const useAddReferrals = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const { mutateAsync: addReferralsMutation, isPending: isAddingReferrals } =
    useMutation<AddReferralsResponse, unknown, void>({
      mutationKey: ["add-referrals", user?.id],
      mutationFn: async () => {
        if (user?.id !== undefined && user?.id !== null) {
          crumb("addReferrals mutation start", { hasUserId: true });

          return userApi.addReferrals(user.id);
        }

        logMsg("addReferrals rejected: no-user-id", "warning", {
          hasUserId: false,
        });
        throw new Error("no-user-id");
      },
      onSuccess: () => {
        crumb("addReferrals success");
        logMsg("addReferrals success", "info", { userId: user?.id ?? null });

        queryClient.removeQueries({ queryKey: [USER_QUERY_KEY] });
        toast.success("Referrals added");
      },
      onError: (error) => {
        logMsg("addReferrals error", "warning", { userId: user?.id ?? null });
        logEx(error, "addReferrals mutation error", { hasUserId: !!user?.id });
        toast.error("Could not add referrals");
      },
    });

  const addReferrals = async () => {
    await addReferralsMutation();
  };

  return {
    addReferrals,
    isAddingReferrals,
  };
};

export { useAddReferrals };
